import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	FormLabel,
	TextField,
	Stack,
	MenuItem,
	Select,
} from "@mui/material";
import React, { useEffect } from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import NumberSpinner from "./NumberSpinner";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { scenarioTitles } from "../Scenarios/Scenarios";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	code: string;
	session: number;
	scenarioIndex: number;
	startTime: Date;
	endTime: Date;
	onCodeChanged: (value: string) => void;
	onSessionChanged: (value: number) => void;
	onScenarioIndexChanged: (value: number) => void;
	onStartTimeChanged: (value: Date) => void;
	onEndTimeChanged: (value: Date) => void;
	onEditSaved: () => void;
}


export default function EditClassDialog(props: Props) {
	const {
		isOpen,
		onClose,
		code,
		session,
		scenarioIndex,
		startTime,
		endTime,
		onCodeChanged,
		onSessionChanged,
		onScenarioIndexChanged,
		onStartTimeChanged,
		onEndTimeChanged,
		onEditSaved } = props;

	const [codeErrorText, setCodeErrorText] = React.useState("");
	const [startTimeErrorText, setStartTimeErrorText] = React.useState("");
	const [endTimeErrorText, setEndTimeErrorText] = React.useState("");

	useEffect(() => {
		if (code !== "") setCodeErrorText("");
		if (startTime !== null) setStartTimeErrorText("");
		if (endTime !== null) setEndTimeErrorText("");
	}, [code, startTime, endTime]);

	const handleOnSave = async () => {
		if (code === "") {
			setCodeErrorText("Class cannot be empty.");
		} else {
			onEditSaved();
			onClose();
		}
	}

	const handleClassUpdate = (event: any) => {
		onCodeChanged(event.target.value);
		setCodeErrorText("");
	}

	const handleSessionUpdate = (value: any) => {
		if (value !== null) onSessionChanged(value);
	}

	const handleScenarioSelect = (event: any) => {
		onScenarioIndexChanged(event.target.value);
	}

	const handleStartTimeChanged = (value: PickerValue) => {
		if (value !== null) {
			onStartTimeChanged(value.second(0).toDate());
			setStartTimeErrorText("");
		} else {
			// Error
			setStartTimeErrorText("This is not a valid date.");
		}
	}

	const handleEndTimeChanged = (value: PickerValue) => {
		if (value !== null) {
			onEndTimeChanged(value.second(0).toDate());
			setEndTimeErrorText("");
		} else {
			// Error
			setEndTimeErrorText("This is not a valid date.");
		}
	}

	return (
		<>
			<Dialog onClose={onClose} open={isOpen}>
				<DialogTitle>Edit Class</DialogTitle>
				<DialogContent sx={{ minWidth: 400 }}>
					<Stack spacing={1}>
						<FormControl>
							<FormLabel htmlFor="class">Class</FormLabel>
							<TextField
								id="class"
								value={code}
								placeholder="Class"
								variant="outlined"
								onChange={handleClassUpdate}
								error={codeErrorText !== ""}
							>
							</TextField>
							<span style={{ color: "#c20a0a", minHeight: 22 }}>{codeErrorText}</span>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="session">Session</FormLabel>
							<NumberSpinner value={session} onValueChange={handleSessionUpdate} min={1} />
							<span style={{ minHeight: 22 }}></span>
						</FormControl>
						<FormControl sx={{ m: 1, minWidth: 160 }}>
							<FormLabel htmlFor="scenario-select">Scenario</FormLabel>
							<Select
								labelId="scenario-label"
								id="scenario-select"
								value={scenarioIndex}
								label="Scenario"
								onChange={handleScenarioSelect}
							>
								{scenarioTitles.map((item, index) => (
									<MenuItem key={index} value={index}>{item}</MenuItem>
								))}
							</Select>
							<span style={{ color: "#c20a0a", minHeight: 22 }}></span>
						</FormControl>
						<Stack direction="row" spacing={1}>
							<FormControl>
								<FormLabel htmlFor="start-time">Start Time</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										onChange={handleStartTimeChanged}
										value={dayjs(startTime)}
									/>
								</LocalizationProvider>
								<span style={{ color: "#c20a0a", minHeight: 22 }}>{startTimeErrorText}</span>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="start-time">End Time</FormLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										onChange={handleEndTimeChanged}
										value={dayjs(endTime)}
									/>
								</LocalizationProvider>
								<span style={{ color: "#c20a0a", minHeight: 22 }}>{endTimeErrorText}</span>
							</FormControl>
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button onClick={handleOnSave}>Save</Button>
				</DialogActions>
			</Dialog>

			{/*  */}
		</>
	)
}